import React from 'react';
import { motion } from 'framer-motion';

const SecuredNavigation = () => {
  return (
    <section 
      id="secured-navigation" 
      className="section-padding" 
      style={{
        background: 'linear-gradient(180deg, #060d1a 0%, #081222 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div 
        className="bg-glow-sphere" 
        style={{ 
          width: '35vw', 
          height: '35vw', 
          background: 'rgba(242, 184, 7, 0.04)', 
          bottom: '-10%', 
          right: '-10%' 
        }} 
      />

      <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div className="split-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '50px',
          alignItems: 'center'
        }}>

          {/* Left Column - Visual Simulation of Path */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="glass-card split-visual"
            style={{
              padding: '30px',
              background: 'rgba(15, 39, 74, 0.25)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              position: 'relative'
            }}
          >
            {/* Visual Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--mga-gold)', fontWeight: 600 }}>🗺️ SIMULATEUR DE TRAJET</span>
              <span style={{ 
                fontSize: '0.75rem', 
                backgroundColor: 'rgba(16, 185, 129, 0.15)', 
                color: 'var(--color-success)', 
                padding: '4px 8px', 
                borderRadius: '6px',
                fontWeight: 600
              }}>
                Trajet Actif
              </span>
            </div>

            {/* Path Graphic */}
            <div style={{ position: 'relative', height: '180px', backgroundColor: 'rgba(5, 11, 20, 0.6)', borderRadius: '12px', padding: '20px', overflow: 'hidden' }}>
              {/* Target Points */}
              <div style={{ position: 'absolute', top: '25px', left: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: 'var(--mga-gold)' }} />
                <span style={{ fontSize: '0.7rem', color: 'var(--mga-slate-400)', marginTop: '4px' }}>Départ A</span>
              </div>

              <div style={{ position: 'absolute', bottom: '25px', right: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#10b981' }} />
                <span style={{ fontSize: '0.7rem', color: 'var(--mga-slate-400)', marginTop: '4px' }}>Arrivée B</span>
              </div>

              {/* Dotted Safe Corridor */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1 }}>
                <path 
                  d="M 36 31 C 120 40, 150 140, 310 145" 
                  fill="none" 
                  stroke="rgba(242, 184, 7, 0.3)" 
                  strokeWidth="24" 
                  strokeLinecap="round"
                />
                <path 
                  d="M 36 31 C 120 40, 150 140, 310 145" 
                  fill="none" 
                  stroke="var(--mga-gold)" 
                  strokeWidth="2" 
                  strokeDasharray="4,4"
                />
              </svg>

              {/* Status Message */}
              <div style={{ 
                position: 'absolute', 
                bottom: '15px', 
                left: '15px', 
                right: '15px',
                backgroundColor: 'rgba(11, 31, 58, 0.85)',
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ animation: 'floatLogo 2s infinite' }}>🛡️</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--mga-white)' }}>
                  Corridor de protection : OK (Pas de déviation)
                </span>
              </div>
            </div>

            {/* Feature Description Inside Visual Column */}
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ fontSize: '1.2rem' }}>🤝</span>
                <span style={{ fontSize: '0.9rem', color: 'var(--mga-slate-300)' }}>
                  <strong>Partage en Direct :</strong> Vos proches suivent vos pas en temps réel.
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ fontSize: '1.2rem' }}>🏁</span>
                <span style={{ fontSize: '0.9rem', color: 'var(--mga-slate-300)' }}>
                  <strong>Auto-validation :</strong> L'alerte se coupe à l'arrivée.
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Presentation */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
          >
            <span className="badge-gold">🚶‍♂️ Rentrer Seul en Sécurité</span>
            <h2 style={{
              fontSize: 'clamp(2rem, 4.5vw, 2.8rem)',
              color: 'var(--mga-white)',
              marginTop: '10px',
              marginBottom: '20px',
              lineHeight: '1.2'
            }}>
              Prévenir le danger. <span style={{ color: 'var(--mga-gold)' }}>Créer un chemin sûr.</span>
            </h2>
            <p style={{ fontSize: '1.05rem', marginBottom: '25px', color: 'var(--mga-slate-300)' }}>
              Quand on rentre seul tard le soir après une soirée, le risque d'agression ou de problème augmente. C'est à ce moment précis que la <strong>navigation sécurisée</strong> prend tout son sens. <strong>Définir un itinéraire fixe est totalement optionnel</strong> : vous pouvez simplement lancer un suivi libre pour partager votre trajet en temps réel avec vos proches et le serveur, tout en gardant actives les sécurités automatiques.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', gap: '15px' }}>
                <div style={{ fontSize: '1.8rem', color: 'var(--mga-gold)' }}>🛣️</div>
                <div>
                  <h4 style={{ color: 'var(--mga-white)', marginBottom: '4px', fontSize: '1.1rem' }}>Corridor de protection (Optionnel)</h4>
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>
                    Si vous définissez un trajet de A à B, l'application surveille si vous déviez de votre chemin et lance automatiquement une pré-alerte si vous sortez de la zone de sécurité définie.
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px' }}>
                <div style={{ fontSize: '1.8rem', color: 'var(--mga-gold)' }}>🛑</div>
                <div>
                  <h4 style={{ color: 'var(--mga-white)', marginBottom: '4px', fontSize: '1.1rem' }}>Détection des arrêts anormaux</h4>
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>
                    Même sans itinéraire prédéfini, si votre progression s'interrompt de manière suspecte et prolongée, le téléphone s'en aperçoit localement et vérifie immédiatement votre état de sécurité.
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px' }}>
                <div style={{ fontSize: '1.8rem', color: 'var(--mga-gold)' }}>📱</div>
                <div>
                  <h4 style={{ color: 'var(--mga-white)', marginBottom: '4px', fontSize: '1.1rem' }}>« Préviens-moi quand tu arrives »</h4>
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>
                    Ne laissez plus vos proches dans l'angoisse d'un message oublié. Votre itinéraire est partagé en temps réel : ils sont notifiés automatiquement dès que vous franchissez votre porte.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default SecuredNavigation;
